import { Stack } from '@bedrock-layout/stack';
import { useAtom } from 'jotai';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { useRef } from 'react';
import { UseControllerProps, UseFormSetValue, useWatch } from 'react-hook-form';
import {
  CheckboxField,
  OnClickRuleArgs,
  PopoverMethods,
  PopoverPanel,
  Typography,
} from 'ui';

import { predefineTokenDatasetAtom } from '../../../../atom';
import { TokenSelectionPopover } from '../../../../components/TokenComponents/TokenSelectionPopover/TokenSelectionPopover';
import { isValidSchemaId } from '../../../../components/TokenComponents/utils/helperFunction';
import { TestNodeTypes } from '../SimpleRule/models';
import { testNodeComponents } from './TestNodeComponents';

type TestNodeFieldProps = Omit<UseControllerProps<any>, 'name'> & {
  index: number;
  nodeKey: string;
  dataType: string;
  isNullable: boolean;
  isOptional?: boolean;
  isDisabled?: boolean;
  nodeType?: 'test' | 'cronAttributes';
  setValue?: UseFormSetValue<any>;
};

function isOfDataType(dataType: string): dataType is TestNodeTypes {
  return [
    'string',
    'numeric',
    'boolean',
    'date',
    'dateTime',
    'json',
    'restAPI',
    'list',
  ].includes(dataType);
}

export function TestNodeField({
  index,
  control,
  nodeKey,
  dataType,
  isNullable,
  isOptional = false,
  isDisabled = false,
  nodeType = 'test',
  setValue,
}: TestNodeFieldProps) {
  const ref = useRef<PopoverMethods>(null);

  const [predefineTokenDataset] = useAtom(predefineTokenDatasetAtom);

  const Component = isOfDataType(dataType)
    ? testNodeComponents[dataType]
    : null;

  if (_isNil(Component)) {
    return null;
  }

  const isDataNull: boolean = useWatch({
    control,
    name: `${nodeType}.${index}.${nodeKey}.isNullable`,
  });

  const isDataOptional: boolean = useWatch({
    control,
    name: `${nodeType}.${index}.${nodeKey}.isOptional`,
  });

  const schemaId = useWatch({
    control,
    name: `${nodeType}.${index}.${nodeKey}.schemaId`,
  });

  const handlePredefineValueSelection = (data: OnClickRuleArgs) => {
    if (!_isNil(setValue)) {
      setValue(`${nodeType}.${index}.${nodeKey}.value`, data.executedValue);
    }

    ref?.current?.hide();
  };

  const launcher = (
    <div style={{ minBlockSize: ' 3rem', minInlineSize: '18rem' }}>
      <Component
        dataType={dataType}
        index={index}
        nodeKey={nodeKey}
        control={control}
        isNullable={isNullable || isOptional}
        disabled={isDataNull || isDataOptional || isDisabled}
        nodeType={nodeType}
        setValue={setValue}
        from="test-sheet"
      />
    </div>
  );

  const isModuleSchemaField =
    !_isNil(schemaId) &&
    !_isEmpty(schemaId) &&
    isValidSchemaId(predefineTokenDataset, schemaId);

  return (
    <Stack gutter={8}>
      {isModuleSchemaField ? (
        <PopoverPanel
          trigger="click"
          placement="bottom-start"
          launcher={launcher}
          ref={ref}
          padding="8px"
          disabled={isDisabled}
        >
          <div>
            <TokenSelectionPopover
              allowSecrets={true}
              selectedSchemaId={schemaId}
              tokenDataset={predefineTokenDataset}
              handleValueSelection={handlePredefineValueSelection}
              tabsToShow={['predefined']}
              showTooltip={['json', 'list'].includes(dataType)}
              typesToAllow={[dataType]}
            />
          </div>
        </PopoverPanel>
      ) : (
        launcher
      )}

      {isNullable && (
        <CheckboxField
          control={control}
          name={`${nodeType}.${index}.${nodeKey}.isNullable`}
          showError
          useId={`${nodeType}.${index}.${nodeKey}.isNullable`}
          label={
            <Typography
              name={isDataOptional ? 'secondaryXs' : 'paragraphSmall'}
            >
              Send null value
            </Typography>
          }
          disabled={isDataOptional || isDisabled}
        />
      )}

      {isOptional && (
        <CheckboxField
          control={control}
          name={`${nodeType}.${index}.${nodeKey}.isOptional`}
          showError
          useId={`${nodeType}.${index}.${nodeKey}.isOptional`}
          label={
            <Typography name="paragraphSmall">
              Don&apos;t send attribute
            </Typography>
          }
          disabled={isDisabled}
        />
      )}
    </Stack>
  );
}
